import { KycProviders } from '@/kyc/types'
import { KYC_STATUS, ORDER_ACTION, ORDER_EXPIRY, ORDER_EXPIRY_MAPPING, ORDER_TYPE, USAGE_POLICIES } from './constants'
import type {
    AccountDetailDto,
    AccountDto,
    FetchUsagePoliciesByAccountResponse,
    UsagePolicyDto,
} from '@six-invest/api-types'

export type Maybe<T> = T | null | undefined

export type UsagePolicyKey = (typeof USAGE_POLICIES)[keyof typeof USAGE_POLICIES]

export type UsagePolicy = UsagePolicyDto

export type AccountUsagePolicies = FetchUsagePoliciesByAccountResponse

export type Address = {
    address: string
    id: string
}

export type AddressDetails = {
    addressId?: string
    addressPrefix?: string | null
    streetNumber: string
    streetName: string
    streetType?: string
    city: string
    state: string
    postCode: string
    country: string
}

export type AddressType = 'residential' | 'postal'

export type TradeRegistryAddressType = 'client' | 'adviser'

export type AddressFormSchema = {
    residential: AddressDetails
    differentPostalAddress?: boolean
    postal: {
        addressPrefix?: string
        streetNumber?: string
        streetName?: string
        streetType?: string
        city?: string
        state?: string
        postCode?: string
        country?: string
    }
}

export interface Name {
    salutation: string
    firstName: string
    middleName?: string | null
    lastName: string
}

export interface ContactDetails {
    salutation: string
    firstName: string
    middleName: string | null
    lastName: string
    dateOfBirth: string
    occupation: string
    mobile: string
}
export type UpdateAddressesPayload = {
    addresses: {
        residentialAddress: AddressDetails
        postalAddress: AddressDetails
    }
}

export enum AccountType {
    INDIVIDUAL = 'INDIVIDUAL',
    JOINT = 'JOINT',
    COMPANY = 'COMPANY',
    INDIVIDUAL_TRUST = 'INDIVIDUAL_TRUST',
    COMPANY_TRUST = 'COMPANY_TRUST',
    ADVISER = 'ADVISER',
}

export enum AccountStatus {
    KYC_NOT_STARTED = 'KYC_NOT_STARTED',
    KYC_IN_PROGRESS = 'KYC_IN_PROGRESS',
    KYC_SUBMITTED = 'KYC_SUBMITTED',
    KYC_FAILED = 'KYC_FAILED',
    AWAITING_CONFIRMATION = 'AWAITING_CONFIRMATION',
    ACCOUNT_SETUP_IN_PROGRESS = 'ACCOUNT_SETUP_IN_PROGRESS',
    ACTIVE = 'ACTIVE',
}

export interface CreateUserPayload {
    salutation?: string
    firstName?: string
    middleName?: string
    lastName?: string
    dateOfBirth?: string
    mobile?: string
    addresses?: {
        residentialAddress: AddressDetails
        postalAddress: AddressDetails
    }
}

export interface User extends ContactDetails {
    accountId?: string | null
    addresses?: {
        residentialAddress: AddressDetails
        postalAddress: AddressDetails
    }
    survey?: {
        investmentPurpose: string
        expectedTradeFrequency: string
        expectedTradeAmount: string
    }
    emailId: string
    userId: string
    createdAt: string
    accountType?: AccountType
    termsAcceptedAt?: string
    nameVerified?: string
    tfnProvided?: boolean
    toDoIds?: string[]
    featurePromotionFlags?: Record<FeaturePromotion, boolean>
    feedbackFlags?: Record<FeedbackId, boolean>
    mfaEnabled?: boolean
}

export enum FeaturePromotion {
    TRUST_ACCOUNTS = 'trust-accounts',
    PWA_APP_INSTALL = 'pwa-app-install',
}

export enum FeedbackId {
    BUNDLE_EXIT_INTENT = 'bundle-exit-intent',
}

export enum RewardType {
    FREE_TRADE_REBATE = 'FREE_TRADE_REBATE',
}

export interface RewardT {
    promotionId: string
    promotionType: RewardType
    name: string
    description?: string
    createdAt: string
    quantityRemaining: number
    quantityTotal: number
    expiresAt?: string
    amount?: string
}

export interface RewardsT {
    items: RewardT[]
}

export type Account = Omit<AccountDto, 'accountType' | 'accountStatus'> & {
    accountType: AccountType
    accountStatus: AccountStatus
}

export type UserAccount = Account & User

/**
 * GET /accounts/{accountId} response. The adviser's trade confirmation email lives at
 * communicationPreferences.contractNotes.email, and the trade registration address is
 * postalAddress - there are no separate tradeContractEmail/tradeRegistrationAddress fields.
 */
export type AccountDetail = Omit<AccountDetailDto, 'accountType' | 'accountStatus'> & {
    accountType: AccountType
    accountStatus?: AccountStatus
}

export interface InitKYC {
    referenceId?: string
    entityId: string
    token: string
    provider: KycProviders
    url?: string
    routing?: {
        target: 'ACCOUNT' | 'PARTICIPANT'
        reason?: 'participant-found' | 'participant-projection-recovered' | 'participant-unresolved' | 'account-only'
    }
}

export type AccountKycParticipant = {
    userId: string
    role: string
    status: keyof typeof KYC_STATUS
}

export type KYCStatus = {
    status: keyof typeof KYC_STATUS
    canInitiateKyc: boolean
    provider: KycProviders
    pin?: string
    kycProviderEntityId: string
    participantsKyc?: AccountKycParticipant[]
}

export type SignupForm = {
    email: string
    password: string
}

export type LoginForm = {
    email: string
    password: string
}

export type InstrumentPrices = {
    lastPrice?: number
    closingPrice?: number
    buyPrice?: number
    percentageChange?: number
    pointsChange?: number
    sellPrice?: number
    status?: string
}

export type InstrumentT = {
    securityName: string
    securityCode: string
    description: string
    priceInfo: InstrumentPrices
}

export type BundleT = {
    slug: string
    name: string
    description: any
    type: 'GROWTH' | 'DEFENSIVE'
    shortDescription?: string
    securities: {
        securityCode: string
        weight: number
        name: string
        summary: string
        priceInfo: InstrumentPrices
    }[]
    performance: {
        year1: string
        year3: string
        year5: string
        lastUpdated: string
        additionalDisclaimer?: string
    }
    returnsLastCalculated: string
    avoided: { avoided: string }[]
    included: { positiveThemesIncluded: string }[]
}

export type SecuritiesBuyOrderStatsT = {
    securityCode: string
    orderCount: number
    unitsCount: number
    name?: string
}

export type InstrumentSearchRecordT = {
    securityName: string
    securityCode: string
}

export type InstrumentWithCampaignMetadataT = InstrumentT & {
    campaignDeadline?: string
}

export type TopMarketDataResponse = {
    page: number
    pageSize: number
    totalCount: number
    companies: InstrumentT[]
}

export type HistoricalPricesRangePreset = '1D' | '1W' | '1M' | '6M' | '1Y' | 'YTD' | '3Y' | '5Y' | 'ALL'

export type PriceDataPoint = {
    close: number
    low?: number
    high?: number
    open?: number
    date: string
    volume?: number
}
export interface EquityT extends Omit<InstrumentT, 'marketCapitalisation'> {
    statistics: {
        marketCapitalisation: number
        volume: number
        peRatio: number
        dividendYield?: string
        dayOpenedAt: number
        dayClosedAt?: number // only after trading close time
        dayRange: [number, number]
        yearRange: [number, number]
    }
    about: {
        ceo: string
        sector: string
        description: string
        history: string
        industry: string
        foundedYear?: string
        webAddress: string
        employees?: number
    }
    securityCode: string
}

export type CampaignStatus = {
    label: string
    description: string
    pillColour: string
}

export type TargetCompanyMetadata = {
    code: string
    campaignDeadline: string
    name: string
    milestones: {
        title: string
        date: string
    }[]
}

export type CampaignT = {
    campaignId: string
    heroImage: {
        url: string
        alt?: string
    }
    title: string
    goals?: string
    description?: string
    targetCompaniesAsxCodes: string[]
    targetCompaniesMetadata?: TargetCompanyMetadata[]
    followers: number
    followersGoal: number
    pledgesGoal: number
    webflowId: string
    countdownDate?: string
    displayOrder?: number
    sixPickSecurityCode: string
    sixPickExplanation: string
    status: CampaignStatus
    onSpotlight?: boolean
}

export type OrderPayload = {
    requestId: string
    securityCode: string
    units: number
    requestedPrice?: number
    orderType: 'MARKET_TO_LIMIT' | 'LIMIT'
    validity:
        | 'VALID_TILL_CANCEL'
        | 'VALID_TILL_EOD'
        | 'VALID_FOR_DAYS_30'
        | 'VALID_FOR_DAYS_7'
        | 'VALID_FOR_DAYS_15'
        | 'VALID_FOR_DAYS_30'
    promotionId?: string
}

export type TradingAccountStatus = 'SUBMITTED' | 'APPROVED' | 'REJECTED'
export type TradingAccountSubmissionData = {
    firstName: string
    middleName: string
    lastName: string
}
export type ContractNoteType = 'CLIENT' | 'ADVISER'
export type TradingAccountDetails = {
    accountNumber: string
    hin: string
    status: TradingAccountStatus
    submissionData: string
    isCashAccount?: boolean
    contractNoteEmails?: {
        email: string
        type: ContractNoteType
    }[]
    registrationAddress?: AddressDetails
}

export type OrderAction = keyof typeof ORDER_ACTION
export type OrderType = keyof typeof ORDER_TYPE

export type OrderExpiry = keyof typeof ORDER_EXPIRY

export type orderValidity = keyof typeof ORDER_EXPIRY_MAPPING

export enum orderStatus {
    REQUESTED = 'REQUESTED',
    'IN MARKET' = 'IN MARKET',
    'PARTIALLY FILLED' = 'PARTIALLY FILLED',
    COMPLETE = 'COMPLETE',
    FAILED = 'FAILED',
    CANCELLED = 'CANCELLED',
}

export type OrderT = {
    orderId: string
    companyName: string
    orderSide: string
    orderType: string
    units: number
    securityCode: string
    validity: orderValidity
    status: orderStatus
    createdAt: string
    expiresOn: string
    orderCompletionPrice?: number
    orderCompletionUnits?: number
    requestedPrice?: number
    promotion?: {
        promotionId: string
        type: string
        amount: number
    }
    failureCode?: string
}

export type HoldingT = {
    securityCode: string
    name: string
    units: number
    averagePrice?: string
    cost?: number
    lastPrice: number
    currentValue: number
    totalChangeValue?: number
    totalChangePercent?: number
    dailyChangeValue?: number
    dailyChangePercent?: number
}

export type ListOrdersResponse = {
    range: {
        from: string
        to: string
    }
    count: number
    orders: OrderT[]
}

export type ListHoldingsResponse = {
    holdings: HoldingT[]
    totalValue: number
    hin: string
}

export type ListUserPledgesResponse = {
    campaignId: string
    securityCode: string
}[]
